import { useState, useEffect } from "react";
import {
  Mail,
  Globe,
  Plus,
  X,
  Save,
  Loader2,
  UserCheck,
  Zap,
  Wrench,
  RefreshCw,
  Key,
  Link,
  FileSignature,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { Button } from "../components/ui";
import {
  fetchDelegateConfig,
  updateDelegateConfig,
  fetchSystemSettings,
  updateSystemSettings,
  fetchEmailAuthStatus,
} from "../services/api";

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

function EmailAuthStatusSection() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchEmailAuthStatus()
      .then(setStatus)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const items = status
    ? [
        { label: "SPF Record", ok: status.spf?.ok, value: status.spf?.value },
        { label: "DKIM", ok: status.dkim?.ok, value: status.dkim?.value },
        { label: "DMARC", ok: status.dmarc?.ok, value: status.dmarc?.value },
        {
          label: "Domain Verification",
          ok: status.domainVerification?.ok,
          value: status.domainVerification?.value,
        },
      ]
    : [];

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Globe size={18} color={colors.accent} />
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              margin: 0,
            }}
          >
            Email Authentication
          </h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            background: "none",
            border: "none",
            cursor: loading ? "default" : "pointer",
            color: colors.textSecondary,
            padding: 4,
            display: "flex",
            alignItems: "center",
          }}
          title="Refresh"
        >
          <RefreshCw
            size={15}
            className={loading ? "spin" : ""}
          />
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      {loading && !status ? (
        <div style={{ textAlign: "center", padding: 12 }}>
          <Loader2
            size={18}
            color={colors.textSecondary}
            className="spin"
          />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((item) => (
            <div
              key={item.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 13, color: colors.textSecondary }}>
                {item.label}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontFamily: fonts.mono,
                  color: item.ok ? colors.success : colors.danger,
                }}
              >
                {item.ok ? "\u2713 " : "\u2717 "}
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmailDelegatesSection() {
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchDelegateConfig()
      .then((data) => {
        setDelegates(data.delegates || []);
        setLastUpdated(
          data.updatedAt ? { at: data.updatedAt, by: data.updatedBy } : null,
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const addDelegate = () => {
    setDelegates([...delegates, { delegateEmail: "", canSendAs: [""] }]);
    setSuccess(null);
  };

  const removeDelegate = (idx) => {
    setDelegates(delegates.filter((_, i) => i !== idx));
    setSuccess(null);
  };

  const updateDelegateEmail = (idx, value) => {
    const updated = [...delegates];
    updated[idx] = { ...updated[idx], delegateEmail: value };
    setDelegates(updated);
    setSuccess(null);
  };

  const addSendAsEmail = (idx) => {
    const updated = [...delegates];
    updated[idx] = {
      ...updated[idx],
      canSendAs: [...updated[idx].canSendAs, ""],
    };
    setDelegates(updated);
    setSuccess(null);
  };

  const updateSendAsEmail = (delegateIdx, emailIdx, value) => {
    const updated = [...delegates];
    const sendAs = [...updated[delegateIdx].canSendAs];
    sendAs[emailIdx] = value;
    updated[delegateIdx] = { ...updated[delegateIdx], canSendAs: sendAs };
    setDelegates(updated);
    setSuccess(null);
  };

  const removeSendAsEmail = (delegateIdx, emailIdx) => {
    const updated = [...delegates];
    updated[delegateIdx] = {
      ...updated[delegateIdx],
      canSendAs: updated[delegateIdx].canSendAs.filter(
        (_, i) => i !== emailIdx,
      ),
    };
    setDelegates(updated);
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    const cleaned = delegates
      .filter((d) => d.delegateEmail.trim())
      .map((d) => ({
        delegateEmail: d.delegateEmail.trim().toLowerCase(),
        canSendAs: d.canSendAs
          .filter((e) => e.trim())
          .map((e) => e.trim().toLowerCase()),
      }))
      .filter((d) => d.canSendAs.length > 0);

    setSaving(true);
    try {
      const result = await updateDelegateConfig(cleaned);
      setDelegates(result.delegates);
      setLastUpdated({ at: result.updatedAt, by: result.updatedBy });
      setSuccess("Delegate configuration saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 24,
          textAlign: "center",
        }}
      >
        <Loader2
          size={18}
          color={colors.textSecondary}
          className="spin"
        />
      </div>
    );
  }

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <UserCheck size={18} color={colors.accent} />
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              margin: 0,
            }}
          >
            Email Delegates
          </h3>
        </div>
        <Button variant="default" onClick={addDelegate}>
          <Plus size={13} /> Add Delegate
        </Button>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Configure which team members can send emails or create drafts on behalf
        of other mailboxes. Each delegate can always send from their own email,
        plus any additional mailboxes listed below.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {delegates.length === 0 ? (
        <div
          style={{
            padding: 20,
            textAlign: "center",
            color: colors.textSecondary,
            fontSize: 13,
            border: `1px dashed ${colors.border}`,
            borderRadius: 6,
          }}
        >
          No delegates configured. Each user can only send from their own
          mailbox.
        </div>
      ) : (
        delegates.map((delegate, dIdx) => (
          <div
            key={dIdx}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <input
                type="email"
                value={delegate.delegateEmail}
                onChange={(e) => updateDelegateEmail(dIdx, e.target.value)}
                placeholder="delegate@example.com"
                style={{ ...inputStyle, flex: 1 }}
              />
              <X
                size={16}
                color={colors.danger}
                style={{ cursor: "pointer", flexShrink: 0 }}
                onClick={() => removeDelegate(dIdx)}
              />
            </div>
            <div
              style={{ fontSize: 11, color: colors.textSecondary, marginBottom: 8 }}
            >
              Can send as:
            </div>
            {delegate.canSendAs.map((email, eIdx) => (
              <div
                key={eIdx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                  paddingLeft: 16,
                }}
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) =>
                    updateSendAsEmail(dIdx, eIdx, e.target.value)
                  }
                  placeholder="principal@example.com"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <X
                  size={14}
                  color={colors.textSecondary}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                  onClick={() => removeSendAsEmail(dIdx, eIdx)}
                />
              </div>
            ))}
            <button
              onClick={() => addSendAsEmail(dIdx)}
              style={{
                background: "none",
                border: "none",
                color: colors.info,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: "pointer",
                padding: "4px 0 0 16px",
              }}
            >
              + Add mailbox
            </button>
          </div>
        ))
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16,
        }}
      >
        {lastUpdated ? (
          <span style={{ fontSize: 11, color: colors.textSecondary }}>
            Last saved {new Date(lastUpdated.at).toLocaleString()} by{" "}
            {lastUpdated.by}
          </span>
        ) : (
          <span />
        )}
        <Button
          variant="primary"
          onClick={handleSave}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          {saving ? (
            <>
              <Loader2
                size={13}
                className="spin"
              />{" "}
              Saving...
            </>
          ) : (
            <>
              <Save size={13} /> Save Delegates
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function SendGridEmailSection() {
  const [form, setForm] = useState({
    sendgridFromEmail: "",
    sendgridFromName: "",
    sendgridReplyTo: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) =>
        setForm({
          sendgridFromEmail: s?.sendgridFromEmail || "",
          sendgridFromName: s?.sendgridFromName || "",
          sendgridReplyTo: s?.sendgridReplyTo || "",
        }),
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const trimmed = {
      sendgridFromEmail: form.sendgridFromEmail.trim(),
      sendgridFromName: form.sendgridFromName.trim(),
      sendgridReplyTo: form.sendgridReplyTo.trim(),
    };
    if (
      trimmed.sendgridFromEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.sendgridFromEmail)
    ) {
      setError("Please enter a valid From email address");
      return;
    }
    if (
      trimmed.sendgridReplyTo &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.sendgridReplyTo)
    ) {
      setError("Please enter a valid Reply-To email address");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateSystemSettings(trimmed);
      setSuccess("Email settings saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Mail size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          Email Configuration
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Configure the sender details for newsletters and email notifications
        sent via SendGrid.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              From Email
            </label>
            <input
              type="email"
              value={form.sendgridFromEmail}
              onChange={(e) => update("sendgridFromEmail", e.target.value)}
              placeholder="e.g. user@example.com"
              style={{ ...inputStyle, maxWidth: 360 }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              From Name
            </label>
            <input
              value={form.sendgridFromName}
              onChange={(e) => update("sendgridFromName", e.target.value)}
              placeholder="e.g. Fordham Global Foresight"
              style={{ ...inputStyle, maxWidth: 360 }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Reply-To Email
            </label>
            <input
              type="email"
              value={form.sendgridReplyTo}
              onChange={(e) => update("sendgridReplyTo", e.target.value)}
              placeholder="e.g. user@example.com"
              style={{ ...inputStyle, maxWidth: 360 }}
            />
          </div>
          <div>
            <Button
              variant="primary"
              size="sm"
              icon={Save}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const DELIVERABILITY_FIELDS = [
  {
    key: "throttleRate",
    label: "Throttle Rate",
    placeholder: "e.g. 200 emails/minute",
  },
  {
    key: "warmupStatus",
    label: "Warm-up Status",
    placeholder: "e.g. Complete",
  },
  {
    key: "unsubscribeHandling",
    label: "Unsubscribe Handling",
    placeholder: "e.g. Automatic (List-Unsubscribe header)",
  },
];

function DeliverabilitySection() {
  const [form, setForm] = useState({
    throttleRate: "",
    warmupStatus: "",
    unsubscribeHandling: "",
  });
  const [softBounceThreshold, setSoftBounceThreshold] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) => {
        const d = s?.deliverability || {};
        setForm({
          throttleRate: d.throttleRate || "",
          warmupStatus: d.warmupStatus || "",
          unsubscribeHandling: d.unsubscribeHandling || "",
        });
        if (typeof s?.softBounceThreshold === "number") {
          setSoftBounceThreshold(s.softBounceThreshold);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const threshold = parseInt(softBounceThreshold, 10);
    if (!threshold || threshold < 1) {
      setError("Soft bounce threshold must be at least 1");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateSystemSettings({
        deliverability: {
          throttleRate: form.throttleRate.trim(),
          warmupStatus: form.warmupStatus.trim(),
          unsubscribeHandling: form.unsubscribeHandling.trim(),
        },
        softBounceThreshold: threshold,
      });
      setSuccess("Deliverability settings saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Zap size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          Deliverability
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Track your email deliverability configuration and status.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {DELIVERABILITY_FIELDS.map((f) => (
            <div key={f.key}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: colors.textSecondary,
                  marginBottom: 4,
                }}
              >
                {f.label}
              </label>
              <input
                value={form[f.key]}
                onChange={(e) => update(f.key, e.target.value)}
                placeholder={f.placeholder}
                style={{ ...inputStyle, maxWidth: 360 }}
              />
            </div>
          ))}

          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Soft Bounce Threshold
            </label>
            <input
              type="number"
              min="1"
              value={softBounceThreshold}
              onChange={(e) => {
                setSoftBounceThreshold(e.target.value);
                setSuccess(null);
                setError(null);
              }}
              style={{ ...inputStyle, maxWidth: 120 }}
            />
            <p
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                marginTop: 4,
                marginBottom: 0,
                lineHeight: 1.4,
              }}
            >
              Contacts reaching this many soft bounces appear in Email Health for review.
              An authorised employee must manually suppress them before they are excluded from future sends.
            </p>
          </div>

          <div>
            <Button
              variant="primary"
              size="sm"
              icon={Save}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ITTeamEmailSection() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) => setEmail(s?.itTeamEmail || ""))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Please enter a valid email address");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateSystemSettings({ itTeamEmail: trimmed });
      setSuccess("IT team email updated");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Wrench size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          IT Team Email
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        The email address that receives webcast creation notifications and tech
        check requests.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setSuccess(null);
              setError(null);
            }}
            placeholder="e.g. services@tlmartin.ltd.uk"
            style={{ ...inputStyle, maxWidth: 320 }}
          />
          <Button
            variant="primary"
            size="sm"
            icon={Save}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

function SendGridApiSection() {
  const [form, setForm] = useState({
    sendgridApiKey: "",
    sendgridWebhookVerificationKey: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) =>
        setForm({
          sendgridApiKey: s?.sendgridApiKey || "",
          sendgridWebhookVerificationKey:
            s?.sendgridWebhookVerificationKey || "",
        }),
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    // Only send non-masked values (masked values contain "••••••")
    const updates = {};
    if (form.sendgridApiKey && !form.sendgridApiKey.includes("••••••")) {
      updates.sendgridApiKey = form.sendgridApiKey.trim();
    }
    if (
      form.sendgridWebhookVerificationKey &&
      !form.sendgridWebhookVerificationKey.includes("••••••")
    ) {
      updates.sendgridWebhookVerificationKey =
        form.sendgridWebhookVerificationKey.trim();
    }
    if (Object.keys(updates).length === 0) {
      setError("No changes to save (masked values are unchanged)");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await updateSystemSettings(updates);
      setForm({
        sendgridApiKey: result?.sendgridApiKey || "",
        sendgridWebhookVerificationKey:
          result?.sendgridWebhookVerificationKey || "",
      });
      setSuccess("API credentials saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Key size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          SendGrid API Credentials
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        API key and webhook verification key from your SendGrid account. These
        override any values set in Azure environment variables.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              SendGrid API Key
            </label>
            <input
              type="password"
              value={form.sendgridApiKey}
              onChange={(e) => update("sendgridApiKey", e.target.value)}
              onFocus={(e) => {
                if (e.target.value.includes("••••••")) e.target.value = "";
                update("sendgridApiKey", "");
              }}
              placeholder="SG.xxxxxxxx..."
              style={{ ...inputStyle, maxWidth: 480, fontFamily: "monospace" }}
            />
            <p
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                marginTop: 4,
                marginBottom: 0,
              }}
            >
              Restricted Access key with Mail Send and Tracking permissions only.
            </p>
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Webhook Verification Key
            </label>
            <input
              type="password"
              value={form.sendgridWebhookVerificationKey}
              onChange={(e) =>
                update("sendgridWebhookVerificationKey", e.target.value)
              }
              onFocus={(e) => {
                if (e.target.value.includes("••••••")) e.target.value = "";
                update("sendgridWebhookVerificationKey", "");
              }}
              placeholder="MFkwEwYHKoZIzj0CAQYIKo..."
              style={{ ...inputStyle, maxWidth: 480, fontFamily: "monospace" }}
            />
            <p
              style={{
                fontSize: 11,
                color: colors.textSecondary,
                marginTop: 4,
                marginBottom: 0,
              }}
            >
              ECDSA public key from SendGrid Settings &rarr; Mail Settings
              &rarr; Event Webhook.
            </p>
          </div>
          <div>
            <Button
              variant="primary"
              size="sm"
              icon={Save}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AppBaseUrlSection() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) => setUrl(s?.appBaseUrl || ""))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const trimmed = url.trim().replace(/\/+$/, "");
    if (trimmed && !/^https?:\/\/.+/.test(trimmed)) {
      setError("Please enter a valid URL starting with https://");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateSystemSettings({ appBaseUrl: trimmed });
      setSuccess("App base URL saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <Link size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          App Base URL
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        The public URL of this application. Used for unsubscribe links, tracking
        pixels, email logos, and other outbound links in newsletters.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              setSuccess(null);
              setError(null);
            }}
            placeholder="e.g. https://app.fgfgeostrategy.com"
            style={{ ...inputStyle, maxWidth: 400 }}
          />
          <Button
            variant="primary"
            size="sm"
            icon={Save}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}

function DocuSealEmailSection() {
  const [form, setForm] = useState({
    docusealFromEmail: "",
    docusealFromName: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSystemSettings()
      .then((s) =>
        setForm({
          docusealFromEmail: s?.docusealFromEmail || "",
          docusealFromName: s?.docusealFromName || "",
        }),
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const trimmed = {
      docusealFromEmail: form.docusealFromEmail.trim(),
      docusealFromName: form.docusealFromName.trim(),
    };
    if (
      trimmed.docusealFromEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.docusealFromEmail)
    ) {
      setError("Please enter a valid email address");
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await updateSystemSettings(trimmed);
      setSuccess("DocuSeal email settings saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setSuccess(null);
    setError(null);
  };

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <FileSignature size={18} color={colors.accent} />
        <h3
          style={{
            fontFamily: fonts.heading,
            fontSize: 16,
            color: colors.text,
            margin: 0,
          }}
        >
          DocuSeal E-Signature Email
        </h3>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Configure the sender address for e-signature requests sent via DocuSeal.
        This should match an O365 shared mailbox or service account configured
        on your self-hosted instance.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      {loading ? (
        <Loader2
          size={16}
          color={colors.textSecondary}
          className="spin"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Sender Email
            </label>
            <input
              type="email"
              value={form.docusealFromEmail}
              onChange={(e) => update("docusealFromEmail", e.target.value)}
              placeholder="e.g. documents@fordhamglobal.com"
              style={{ ...inputStyle, maxWidth: 360 }}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: colors.textSecondary,
                marginBottom: 4,
              }}
            >
              Sender Name
            </label>
            <input
              value={form.docusealFromName}
              onChange={(e) => update("docusealFromName", e.target.value)}
              placeholder="e.g. Fordham Global Foresight"
              style={{ ...inputStyle, maxWidth: 360 }}
            />
          </div>
          <div>
            <Button
              variant="primary"
              size="sm"
              icon={Save}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ITAdmin() {
  return (
    <div>
      <h1
        style={{
          fontFamily: fonts.heading,
          fontSize: 28,
          fontWeight: 700,
          color: colors.text,
          marginBottom: 4,
        }}
      >
        IT Administration
      </h1>
      <p
        style={{
          fontFamily: fonts.body,
          fontSize: 14,
          color: colors.textSecondary,
          marginBottom: 32,
        }}
      >
        Email authentication, configuration, deliverability, and delegate
        management
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
          maxWidth: 640,
        }}
      >
        <EmailAuthStatusSection />
        <SendGridApiSection />
        <SendGridEmailSection />
        <AppBaseUrlSection />
        <DocuSealEmailSection />
        <DeliverabilitySection />
        <ITTeamEmailSection />
        <EmailDelegatesSection />
      </div>    </div>
  );
}
